import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FroalaEditorComponent from "react-froala-wysiwyg";
import Autocomplete from "@mui/material/Autocomplete";
import { fetchPolicyLabels } from "../../api/faqApi";
import CommonSnackbar from "../CommonSnackbar";

interface EditPolicyDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    label: string;
    label_id: string;
    description: string;
  }) => void;
  initialValues?: {
    label?: string;
    label_id?: string;
    description?: string;
    programName?: string;
  };
  loading?: boolean;
}

function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

function sanitizeHtml(html: string): string {
  if (!html || typeof html !== "string") return "";

  try {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.innerHTML;
  } catch (error) {
    console.error("Error sanitizing HTML:", error);
    return "";
  }
}

const EditPolicyDialog: React.FC<EditPolicyDialogProps> = ({
  open,
  onClose,
  onSave,
  initialValues = {},
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    label: initialValues.label || "",
    description: initialValues.description || "",
    label_id: initialValues.label_id || "",
  });
  const [errors, setErrors] = useState({
    label: false,
    description: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [labelOptions, setLabelOptions] = useState<
    { label: string; value: string; label_id: string }[]
  >([]);
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [editorReady, setEditorReady] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (open && initialValues) {
      setTimeout(() => {
        setFormData({
          label: initialValues.label || "",
          description: sanitizeHtml(initialValues.description || ""),
          label_id: initialValues.label_id || "",
        });
        setEditorReady(true);
        setEditorKey((prev) => prev + 1);
      }, 100);
    } else if (!open) {
      // Reset form when dialog closes
      setEditorReady(false);
      setTimeout(() => {
        setFormData({
          label: "",
          description: "",
          label_id: "",
        });
        setErrors({
          label: false,
          description: false,
        });
        setEditorKey((prev) => prev + 1);
      }, 100);
    }
  }, [initialValues, open]);

  useEffect(() => {
    if (open) {
      setLoadingLabels(true);
      fetchPolicyLabels()
        .then((labels) => setLabelOptions(labels))
        .catch(() => setLabelOptions([]))
        .finally(() => setLoadingLabels(false));
    }
  }, [open]);

  const validateForm = () => {
    const newErrors = {
      label: !formData.label.trim(),
      description: !formData.description.trim(),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleClose = () => {
    setEditorReady(false);
    setTimeout(() => {
      onClose();
    }, 100);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "60vw",
          height: "85vh",
          maxWidth: "60vw",
          maxHeight: "85vh",
          p: 0,
          overflow: "visible",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#f7f7f7",
          fontWeight: 700,
          fontSize: 20,
          pb: 2,
          pt: 3,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #eee",
          borderRadius: "6px 6px 0 0",
        }}
      >
        Edit Policy
        <IconButton onClick={handleClose} sx={{ ml: 2 }}>
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 3, overflowY: "auto", flexGrow: 1 }}>
        {/* Program Name (read-only) */}
        <Box sx={{ my: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
            Program <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            value={initialValues?.programName || ""}
            label="Program"
            placeholder="Program name"
            variant="outlined"
            sx={{ background: "#f5f5f5" }}
            InputProps={{ readOnly: true }}
            fullWidth
          />
        </Box>
        <Box sx={{ my: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
            Label <span style={{ color: "red" }}>*</span>
          </Typography>
          <Autocomplete
            options={labelOptions}
            getOptionLabel={(option) => option.label}
            loading={loadingLabels}
            value={
              labelOptions.find((l) => l.label_id === formData.label_id) || null
            }
            onChange={(_, newValue) => {
              setFormData((prev) => ({
                ...prev,
                label: newValue ? newValue.label : "",
                label_id: newValue ? newValue.label_id : "",
              }));
              if (errors.label)
                setErrors((prev) => ({ ...prev, label: false }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Label"
                placeholder="Select a label"
                variant="outlined"
                sx={{ background: "#fff" }}
                error={errors.label}
                helperText={errors.label ? "Label is required" : ""}
              />
            )}
            fullWidth
          />
        </Box>
        <Box sx={{ my: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
            Description <span style={{ color: "red" }}>*</span>
          </Typography>
          {editorReady ? (
            <FroalaEditorComponent
              key={`editor-${editorKey}`}
              tag="textarea"
              model={formData.description || ""}
              onModelChange={(content: string) => {
                try {
                  setFormData((prev) => ({ ...prev, description: content }));
                  if (errors.description)
                    setErrors((prev) => ({ ...prev, description: false }));
                } catch (error) {
                  console.error("Error in onModelChange:", error);
                }
              }}
              config={{
                placeholderText: "Edit description",
                charCounterCount: false,
                heightMin: 120,
                heightMax: 230,
                toolbarSticky: false,
                initOnClick: false,
                destroyOnClose: true,
                events: {
                  "froalaEditor.destroy": function () {
                    console.log("Editor destroyed");
                  },
                  "froalaEditor.error": function (
                    e: any,
                    editor: any,
                    error: any
                  ) {
                    console.error("Froala error:", error);
                  },
                },
              }}
            />
          ) : (
            <div
              style={{
                height: "120px",
                border: "1px solid #ddd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Loading editor...
            </div>
          )}
          {errors.description && (
            <Typography color="error" variant="caption">
              Description is required
            </Typography>
          )}
        </Box>
      </DialogContent>
      <Box sx={{ borderTop: "1px solid #eee" }}>
        <DialogActions sx={{ p: 2, display: "flex", gap: 2 }}>
          <Button
            onClick={handleClose}
            sx={{
              color: "#000",
              border: "1px solid #000",
              width: 106,
              height: 38,
            }}
            disabled={isSaving || loading}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!validateForm()) return;
              setIsSaving(true);
              try {
                await onSave({
                  label: formData.label,
                  label_id: formData.label_id,
                  description: stripHtml(formData.description),
                });
                // Dialog will be closed by parent component on success
              } catch (error: any) {
                setSnackbar({
                  open: true,
                  message:
                    error?.response?.data?.message ||
                    error?.message ||
                    "Failed to update policy. Please try again.",
                  severity: "error",
                });
              } finally {
                setIsSaving(false);
              }
            }}
            sx={{
              color: "#fff",
              background: "#000",
              "&:hover": {
                background: "#333",
              },
              position: "relative",
              width: 106,
              height: 38, // Match MUI Button default height
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            disabled={isSaving || loading}
          >
            {isSaving ? (
              <CircularProgress
                size={24}
                sx={{
                  color: "#fff",
                }}
              />
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Box>
      <CommonSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Dialog>
  );
};

export default EditPolicyDialog;
